import axios from 'axios';
import { Message, MessageItem, Comment, CallTranscript, AiInfoCustomer, AssistConversation, AssistMessage, ErrorReport, ToolTraceEntry } from '../types';
import { apiConfig } from '../config/auth';
import { getOrgUsers, getRoleAssignedUserIds } from './graph';

const api = axios.create({
  baseURL: apiConfig.baseUrl,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Lightweight global toast: a component registers a handler; API errors raise it.
type ToastFn = (message: string, kind?: 'error' | 'info') => void;
let toastFn: ToastFn | null = null;
export const setToastHandler = (fn: ToastFn | null) => { toastFn = fn; };
export const showToast = (message: string, kind: 'error' | 'info' = 'error') => {
  if (toastFn) toastFn(message, kind);
};

// Global loading indicator: a component registers a handler; we drive it from a
// counter of in-flight ACTION requests. "Action" = a mutating call (POST/PUT/
// PATCH/DELETE) that is NOT flagged background. We deliberately EXCLUDE every GET
// (the 4s feed poll, SignalR reloads, badge/list polls — the feed owns its own
// skeleton for those) and any request tagged `{ background: true }` (the thread-
// viewing heartbeat), so the bar reflects user actions and never flashes on the
// idle poll. Counting both success AND error paths keeps the counter honest so
// the bar can never stick on (a stuck bar reads as a frozen app).
type LoadingFn = (active: boolean) => void;
let loadingFn: LoadingFn | null = null;
export const setLoadingHandler = (fn: LoadingFn | null) => { loadingFn = fn; };
let inFlight = 0;
const emitLoading = () => { if (loadingFn) loadingFn(inFlight > 0); };
const noteStart = () => { inFlight += 1; if (inFlight === 1) emitLoading(); };
const noteEnd = () => { inFlight = Math.max(0, inFlight - 1); if (inFlight === 0) emitLoading(); };

const _countsAsAction = (config: any): boolean => {
  if (!config || config.background) return false;            // explicit opt-out (heartbeat)
  const m = String(config.method || 'get').toLowerCase();
  return m === 'post' || m === 'put' || m === 'patch' || m === 'delete';
};

// Token provider: App.tsx registers a function that returns a FRESH API access
// token via MSAL acquireTokenSilent — a cache hit is instant, and MSAL renews
// silently (via the SPA refresh token) once the cached token nears/passes expiry.
// Attaching a fresh token PER REQUEST means the bearer is never the stale
// login-time token, so ordinary token rollover (Azure re-issues on its own
// lifetime cadence) is invisible. The "sign-in expired" toast then only fires
// when renewal genuinely fails — i.e. the SPA refresh token itself has died,
// which is the real ~24h session ceiling, not a 20-minute access-token rollover.
type TokenProvider = (opts?: { forceRefresh?: boolean }) => Promise<string | null>;
let tokenProvider: TokenProvider | null = null;
export const setTokenProvider = (fn: TokenProvider | null) => { tokenProvider = fn; };

api.interceptors.request.use(async (config) => {
  // Guard on __counted so a 401-retry (same config object) doesn't double-count.
  if (_countsAsAction(config) && !(config as any).__counted) {
    (config as any).__counted = true;
    noteStart();
  }
  // Attach a current token. On a cache hit this is effectively free; MSAL only
  // hits the network when the token needs renewing. On failure we leave whatever
  // default bearer is set — the 401 response path below owns forced recovery.
  if (tokenProvider) {
    try {
      const token = await tokenProvider();
      if (token) {
        (config.headers as any) = config.headers || {};
        (config.headers as any).Authorization = 'Bearer ' + token;
      }
    } catch { /* keep default bearer; the 401 interceptor will force a renewal */ }
  }
  return config;
});

// Surface permission/auth failures to the user instead of failing silently, and
// release the loading counter for any request we started counting. On a 401 we
// attempt ONE forced silent renewal + retry BEFORE toasting, so an ordinary
// access-token rollover never reaches the user; only a genuine renewal failure
// (dead refresh token) surfaces the "please sign in again" prompt.
api.interceptors.response.use(
  (r) => {
    if ((r.config as any)?.__counted) noteEnd();
    return r;
  },
  async (error) => {
    const config = error?.config;
    const status = error?.response?.status;

    if (status === 401 && config && !(config as any).__retried401 && tokenProvider) {
      (config as any).__retried401 = true;                   // guard against renew->still-401 loop
      try {
        const token = await tokenProvider({ forceRefresh: true });
        if (token) {
          setAuthToken(token);                               // refresh the default bearer too
          (config.headers as any) = config.headers || {};
          (config.headers as any).Authorization = 'Bearer ' + token;
          return api.request(config);                        // retry once; counter stays held, retry resolves it
        }
      } catch { /* fall through to the toast below */ }
    }

    if ((config as any)?.__counted) noteEnd();
    if (status === 403) {
      showToast("You don't have access to do that. Ask an admin to assign you a role.");
    } else if (status === 401) {
      showToast('Your sign-in expired. Please refresh the page and sign in again.');
    }
    return Promise.reject(error);
  }
);

export const setAuthToken = (token: string) => {
  api.defaults.headers.common['Authorization'] = 'Bearer ' + token;
};

// The signed-in user (from MSAL). Used to attribute who sent a manual reply or
// posted a comment, so outbound messages can be stamped with the real sender.
let currentUser: { name: string; email: string; roles: string[] } = { name: '', email: '', roles: [] };

export const setCurrentUser = (name?: string | null, email?: string | null, roles?: string[] | null) => {
  currentUser = { name: name || '', email: email || '', roles: roles || [] };
};

export const getCurrentUser = () => currentUser;

// App Roles come from the token (SALES / MANAGER / ADMIN). Managers and admins
// can dispatch new-message notifications and assign threads to other people.
export const isManager = () => currentUser.roles.some((r) => r === 'MANAGER' || r === 'ADMIN');

// Admin-only gate (distinct from isManager, which is MANAGER or ADMIN). True
// only when the current user's app roles include ADMIN.
export const isAdmin = () => currentUser.roles.some((r) => r === 'ADMIN');

// Thread presence ping: signal the backend that a human is viewing (expanded)
// or has released (collapsed) a sub-thread, so the AI stays silent while a human
// has it open and resumes when they collapse it. This is a PRESENCE PING — it
// must NEVER throw into the UI, so failures are caught and ignored. The `by`
// attribution is the signed-in user's name when known (else omitted).
export const setThreadViewing = async (threadId: number, viewing: boolean): Promise<any> => {
  try {
    const response = await api.post('/threads/' + threadId + '/viewing', {
      viewing,
      by: getCurrentUser()?.name || undefined
    }, { background: true } as any);  // presence ping — never drives the global loading bar
    return response.data;
  } catch (e) {
    /* non-fatal: a presence ping must never disrupt the UI */
    return undefined;
  }
};

// Move a mis-threaded inbound message into a different thread (human override of
// the AI's thread placement).
export const reassignMessageThread = async (messageId: number, threadId: number): Promise<any> => {
  const r = await api.post('/messages/' + messageId + '/reassign-thread', { thread_id: threadId });
  return r.data;
};

// Split a mis-threaded inbound message OUT into its OWN brand-new thread (human
// override when the message belongs to no existing thread). Same endpoint as the
// move, with the {new_thread:true} sentinel; the message becomes its own root.
export const splitMessageToNewThread = async (messageId: number): Promise<any> => {
  const r = await api.post('/messages/' + messageId + '/reassign-thread', { new_thread: true });
  return r.data;
};

// Messages
export const getMessages = async (params?: Record<string, string>): Promise<Message[]> => {
  const response = await api.get('/messages', { params });
  // Guard: a malformed/partial payload (or an HTML error body) must not become a
  // non-array that then crashes the feed's .map. Mirrors the list peers below.
  const d = response.data;
  return Array.isArray(d) ? d : [];
};

export const getMessage = async (id: number): Promise<Message> => {
  const response = await api.get('/messages/' + id);
  return response.data;
};

// Status-only update. Assignment/ownership was removed with the SR-team-lead
// Wave A change (assign / My Queue / View-as all gone), so this no longer carries
// assignedTo/assignedToName — it PATCHes the message status alone.
export const updateMessageStatus = async (id: number, status: string): Promise<Message> => {
  const response = await api.patch('/messages/' + id, { status });
  return response.data;
};

// Flag (or clear) a message for human review.
export const setNeedsReview = async (messageId: number, value: boolean): Promise<any> => {
  const response = await api.patch('/messages/' + messageId, { needsReview: value });
  return response.data;
};

// Captured media (Phase 6.2 image intake). Streams the RAW bytes of one media
// reference (image or PDF) from the MSAL-authed GET /api/media/{id} endpoint —
// NO SAS / public blob URL ever reaches the browser. Goes through the shared
// axios `api` client (carries the MSAL bearer + the 401/403 interceptor), with
// responseType 'blob' so we get bytes, and `{ background: true }` so the fetch
// does NOT drive the global loading bar (it's a lazy per-thumbnail read, like
// the presence-ping heartbeat). Returns the Blob; the caller makes an object URL
// and is responsible for revoking it.
export const getMedia = async (id: number): Promise<Blob> => {
  const response = await api.get('/media/' + id, {
    responseType: 'blob',
    background: true,
  } as any);
  return response.data as Blob;
};

// Message Items
export const getMessageItems = async (messageId: number): Promise<MessageItem[]> => {
  const response = await api.get('/messages/' + messageId + '/items');
  return response.data;
};

// ONE sweep of the WHOLE thread (scope=thread): every distinct order line in the
// conversation, deduped server-side, so the SO preview reflects the entire thread
// rather than a single message's items. The thread is derived from the message id.
export const getThreadOrderItems = async (messageId: number, group?: number): Promise<MessageItem[]> => {
  const params: any = { scope: 'thread', kind: 'SO' };
  if (group != null) params.group = group;
  const response = await api.get('/messages/' + messageId + '/items', { params });
  return response.data;
};

// RMA preview: the thread's RETURN lines only (kind=RMA). Same thread sweep as
// getThreadOrderItems but filtered server-side to line_kind='RMA'.
export const getThreadRmaItems = async (messageId: number): Promise<MessageItem[]> => {
  const response = await api.get('/messages/' + messageId + '/items', { params: { scope: 'thread', kind: 'RMA' } });
  return response.data;
};

// Move a return line to an RMA group (which Return Authorization it belongs to).
// Splits a thread's returns into multiple RMAs. PATCH {line_id, rma_group}.
export const moveRmaLine = async (messageId: number, lineId: number, rmaGroup: number): Promise<any> => {
  const response = await api.patch('/messages/' + messageId + '/items', { line_id: lineId, rma_group: rmaGroup });
  return response.data;
};

// ---------------------------------------------------------------------------
// Multi-preview tabs: multiple staged Orders / RMAs / Credit Memos per thread.
// A preview instance = (thread, type, group_no). The preview_groups registry
// (backend) is the tab list + home of the per-preview customer override.
// ---------------------------------------------------------------------------
export type PreviewType = 'SO' | 'RMA' | 'CM';

// One tab entry from the registry (scope=groups). line_count = staged lines on
// that tab; customer_* = per-preview override (null => inherit thread customer).
export interface PreviewGroup {
  line_kind: PreviewType;
  group_no: number;
  line_count: number;
  customer_id?: number | null;
  customer_name?: string | null;
  customer_locked?: boolean;
  label?: string | null;
  split_reason?: string | null;
}

// The full tab registry for a thread (all types). Powers the tab strips + the
// "+ Add preview" list. NON-FATAL ([] on error).
export const listPreviewGroups = async (messageId: number): Promise<PreviewGroup[]> => {
  try {
    const r = await api.get('/messages/' + messageId + '/items', { params: { scope: 'groups' } });
    return r.data || [];
  } catch (e) {
    return [];
  }
};

// Add a NEW, EMPTY preview instance (tab) of a type to the thread ("+ Add
// preview"). Registers the next free group_no; inserts no line; no NetSuite.
// Optionally seeds a per-preview customer. Returns { group_no }.
export const createPreviewGroup = async (
  messageId: number,
  type: PreviewType,
  opts?: { customer_id?: number | null; customer_name?: string | null; label?: string | null }
): Promise<{ group_no: number }> => {
  const response = await api.post('/messages/' + messageId + '/items', {
    op: 'create_preview', line_kind: type, ...(opts || {}),
  });
  return { group_no: response.data.group_no };
};

// STAGING-ONLY removal of a whole preview instance (the tab ×). Deletes the
// group's staged lines + registry row + header prefs. Does NOT cancel any
// NetSuite order/RMA/CM (that stays the explicit "Cancel order" action).
export const deletePreviewGroup = async (
  messageId: number, type: PreviewType, groupNo: number
): Promise<any> => {
  const response = await api.post('/messages/' + messageId + '/items', {
    op: 'delete_preview', line_kind: type, group_no: groupNo,
  });
  return response.data;
};

// Reassign ONE preview's customer (defaults to the thread customer). Re-resolves
// that preview's lines against the new customer server-side (customer-specific
// shorthand/history/pricing). App-DB write only; does NOT re-stamp the thread.
export const setPreviewCustomer = async (
  messageId: number, type: PreviewType, groupNo: number,
  customerId: number, customerName?: string | null
): Promise<any> => {
  const response = await api.post('/messages/' + messageId + '/preview-customer', {
    line_kind: type, group_no: groupNo, customer_id: customerId, customer_name: customerName,
  });
  return response.data;
};

// Comments
export const getComments = async (messageId: number): Promise<Comment[]> => {
  const response = await api.get('/messages/' + messageId + '/comments');
  return response.data;
};

export const postComment = async (messageId: number, data: { userEmail: string; userName: string; commentText: string }): Promise<{ id: number }> => {
  const response = await api.post('/messages/' + messageId + '/comments', data);
  return response.data;
};

// "Sally needs help" note: ask the backend to (idempotently) post Sally's detailed,
// grounded explanation of what she's stuck on into the thread's Comments. Fired
// best-effort when a rep opens a flagged thread. Server-side it no-ops (no model
// call) when the thread isn't stuck or Sally's already commented since the last
// message, so calling it on every open is safe. Returns the status dict.
export const ensureSallyHelpNote = async (threadRootId: number): Promise<{ ok: boolean; posted: boolean; skipped?: string }> => {
  const response = await api.post('/threads/' + threadRootId + '/help-note', {});
  return response.data;
};

// AI Assist chat (replaces Team Chat). A Claude-app-style troubleshooting
// companion. The caller's ROLE (from the validated MSAL principal) drives the
// server-side tool set — the frontend never gates tools, it just renders the
// conversation. Mirrors the other typed calls: through the shared axios `api`
// client so the MSAL bearer + 401/403 interceptor apply; leading-slash paths.

// Send a turn. conversationId omitted -> the backend starts a new conversation
// and returns its id. threadRef attaches the chat to a SLX thread (messages.id
// root) so the AI can read that thread's substrate. The reply may carry a
// createdReportId when the loop escalated an error report during the turn.
export const sendAssistMessage = async (body: {
  conversationId?: number;
  threadRef?: number;
  message: string;
}): Promise<{
  conversationId: number;
  reply: string;
  // A light trace of the tools the loop ran this turn (Revision 2). Used by the
  // UI only for visual affordances (e.g. detecting a force-correct) — raw trace
  // JSON is never surfaced to the user. Tolerant of a bare array or missing.
  toolTrace?: ToolTraceEntry[];
  createdReportId?: number | null;
}> => {
  const response = await api.post('/ai-assist/chat', body);
  const d = response.data || {};
  return {
    conversationId: d.conversationId,
    reply: d.reply || '',
    toolTrace: Array.isArray(d.toolTrace) ? d.toolTrace : undefined,
    createdReportId: d.createdReportId ?? null,
  };
};

// The caller's conversations (admins may see all). Guarded to [] so a
// partial/failed response degrades gracefully in the left rail.
export const getAssistConversations = async (): Promise<AssistConversation[]> => {
  const response = await api.get('/ai-assist/conversations');
  const d = response.data;
  if (Array.isArray(d)) return d;
  return Array.isArray(d?.conversations) ? d.conversations : [];
};

// Turns for one conversation (owner or admin).
export const getAssistMessages = async (conversationId: number): Promise<AssistMessage[]> => {
  const response = await api.get('/ai-assist/conversations/' + conversationId + '/messages');
  const d = response.data;
  if (Array.isArray(d)) return d;
  return Array.isArray(d?.messages) ? d.messages : [];
};

// Error reports. Non-admins see their own; admins see all. Returns the list plus
// a count (used for the admin badge). The backend nests under `reports`/`count`;
// we also accept a bare array so a shape change on either side degrades safely.
export const getErrorReports = async (params?: {
  status?: string;
}): Promise<{ reports: ErrorReport[]; count: number }> => {
  const response = await api.get('/error-reports', { params: params || {} });
  const d = response.data;
  if (Array.isArray(d)) return { reports: d, count: d.length };
  const reports: ErrorReport[] = Array.isArray(d?.reports) ? d.reports : [];
  return { reports, count: typeof d?.count === 'number' ? d.count : reports.length };
};

// Admin-only triage of an error report (status/assign/fix_spec/admin_notes). The
// backend enforces ADMIN; the frontend gate is UX-only. Errors are SURFACED so
// the inbox can react (the 401/403 interceptor also toasts auth failures).
export const updateErrorReport = async (
  id: number,
  body: Partial<{
    status: string;
    assigned_admin_email: string;
    fix_spec: string;
    admin_notes: string;
  }>
): Promise<ErrorReport> => {
  const response = await api.patch('/error-reports/' + id, body);
  // The backend wraps the updated row in an envelope ({success, report}); accept
  // either the envelope or a bare report so the inbox's row merge keeps id/status.
  return (response.data && response.data.report) ? response.data.report : response.data;
};

// Corrections
export const submitCorrection = async (itemId: number, correctedItemId: number, correctedQuantity?: number): Promise<void> => {
  await api.post('/corrections', { itemId, correctedItemId, correctedQuantity });
};

// Approve message
export const approveMessage = async (messageId: number): Promise<void> => {
  await api.post('/messages/' + messageId + '/approve');
};

// Shorthand
export const addShorthand = async (shorthand: string, itemId: number): Promise<void> => {
  await api.post('/shorthand', { shorthand, itemId });
};

// Map preview lines to the NetSuite SO line payload. A normal line sends
// {item_id, quantity} and lets NetSuite auto-price. A DISCOUNT/MARKETING credit
// line sends quantity 1 with a NEGATIVE rate (the app stores the credit positive;
// NetSuite gets it negative so it shows as a credit against the SO total). A
// DROP-SHIP (direct-ship) line carries create_po + po_vendor_id so NetSuite spawns
// the linked PO (reproduces a direct-ship order).
const toSoLinePayload = (items: MessageItem[]) =>
  items
    .filter(i => i.item_id)
    .map(i => {
      const isCredit = i.line_kind === 'DISCOUNT' || i.line_kind === 'MARKETING';
      if (isCredit) {
        const amt = Math.abs(Number(i.credit_amount) || 0);
        return { item_id: i.item_id, quantity: 1, rate: -amt };
      }
      const line: any = { item_id: i.item_id, quantity: i.quantity || 1 };
      // Per-line unit price ("Price each"). When set, sent as the NetSuite line
      // rate so the shown/edited price is exactly what the SO gets (a manual price
      // edit like the drop-ship shipping adjustment is honored); otherwise omitted
      // so NetSuite auto-prices.
      if (i.unit_price != null && !Number.isNaN(Number(i.unit_price))) {
        line.rate = Number(i.unit_price);
      }
      if (i.create_po) {
        line.create_po = true;
        if (i.po_vendor_id) line.po_vendor_id = i.po_vendor_id;
      }
      return line;
    });

// ── SO-preview parity (SO-PARITY-v1) ─────────────────────────────────────────
// The editable header fields the preview sends so a placed SO matches a normal
// NetSuite order (verified against SO119111) without post-hoc corrections.
export interface SoHeaderFields {
  location_id?: string | null;
  ship_method_id?: string | null;
  ship_address_id?: string | null;
  terms_id?: string | null;
  sales_rep_id?: string | null;
  custom_form_id?: string | null;
  message?: string | null;
  shipping_cost?: number | null;
  ship_date?: string | null;   // YYYY-MM-DD
  route_day?: string | null;   // weekday name, e.g. "Thursday"
  load_type_rank_id?: string | null;
  truckload_slot_weight?: number | null;
  pallet_jack_needed?: boolean;
  seal_trailer?: boolean;
  ship_blind_order?: boolean;
  // "Automatically Apply Promotions" (mirrors NetSuite's SO Promotions-subtab
  // checkbox). Default unchecked. When true, the backend evaluates which promos
  // NetSuite would auto-apply to this order and applies them; nothing applies unless
  // this is checked (or a manual promotion line is added). Sent hoisted to a
  // top-level create field, NOT into NetSuite custom_fields.
  autoApplyPromotions?: boolean;
}

export interface SoOption { id: string; name: string; fullname?: string }
export interface SoAddress {
  id: string; label: string; addr1?: string | null; city?: string | null;
  state?: string | null; zip?: string | null;
  default_shipping: boolean; default_billing: boolean;
}
// Ship-to gate verdict (2026-07-28): whether the customer has a usable VALIDATED
// ship-to address. 'ok' -> address_id is the one to use; 'ambiguous' -> the rep
// must pick from candidates; 'none' -> no validated address on file (blocked);
// 'error' -> could not verify (blocked, transient).
export interface SoShipTo {
  status: 'ok' | 'ambiguous' | 'none' | 'error';
  address_id?: string | null;
  candidates: SoAddress[];
}
export interface SoPreviewBundle {
  defaults: {
    salesrep_id?: string; salesrep_name?: string | null;
    terms_id?: string; terms_name?: string;
    route_day_id?: string; route_day_name?: string;
    default_ship_address_id?: string;
  };
  addresses: SoAddress[];
  ship_to?: SoShipTo;
  terms: SoOption[];
  locations: SoOption[];
  shipMethods: SoOption[];
  salesReps: SoOption[];
  // Company departments — the CM line's DEPARTMENT dropdown source.
  departments: SoOption[];
  loadTypeRanks: SoOption[];
  routeDays: SoOption[];
  // Active promotions for the manual "Add Promotion" line picker (mirrors NetSuite's
  // Promotions sublist). Optional (older bundles won't have it).
  promotions?: PromotionOption[];
}

// A NetSuite promotion for the manual promotion-line picker.
export interface PromotionOption {
  id: string;
  code?: string | null;
  name: string;
  promotiontype?: string | null;   // ITEM | ORDER | SHIPPING | FIXEDPRICE
  discounttype?: string | null;    // percent | flat
  rate?: string | null;
}

// Persisted SO-preview manual corrections (header fields, hot load, per-line price
// + direct-ship), keyed by the order message id, so closing/reopening a thread
// doesn't lose a rep's edits. Shape is opaque JSON: {header, hot_load, lines}.
export const getSoPreviewPrefs = async (messageId: number, groupNo: number = 1): Promise<any> => {
  const r = await api.get('/netsuite/sopreviewprefs/' + messageId, { params: { group: groupNo } });
  return (r.data && r.data.prefs) || {};
};
export const saveSoPreviewPrefs = async (messageId: number, prefs: any, groupNo: number = 1): Promise<void> => {
  await api.put('/netsuite/sopreviewprefs/' + messageId, { prefs, group: groupNo });
};

// One call: every dropdown option list (+ the customer's default header values
// when a customer is linked). The customer id is optional — the option lists are
// account-global, so they populate even on an unlinked thread.
export const getSoPreviewBundle = async (customerId?: number | null): Promise<SoPreviewBundle> => {
  const r = await api.get('/netsuite/sopreview' + (customerId ? '/' + customerId : ''));
  return r.data;
};

// Per-item NetSuite weight (lb) AND list price → {item_id: value}, in ONE call.
// weight is the authoritative source for the SO-preview total (Σ weight × qty);
// price prefills each line's editable "Price each" (the line rate).
export const getItemWeightsAndPrices = async (
  ids: number[]
): Promise<{ weights: Record<number, number>; prices: Record<number, number> }> => {
  const clean = Array.from(new Set(ids.filter(n => Number.isFinite(n))));
  if (clean.length === 0) return { weights: {}, prices: {} };
  const r = await api.get('/netsuite/itemweights', { params: { ids: clean.join(',') } });
  const num = (obj: any): Record<number, number> => {
    const out: Record<number, number> = {};
    Object.entries(obj || {}).forEach(([k, v]) => { out[Number(k)] = Number(v); });
    return out;
  };
  return { weights: num(r.data.weights), prices: num(r.data.prices) };
};

// ── Customer price-level + quantity-break pricing (LEVELED-PRICE-v1) ─────────
// The price NetSuite will actually apply for THIS customer: their price LEVEL
// (customer.pricelevel) read from the item's per-level pricing, choosing the
// quantity-break tier for the ordered qty. Drives the SO-preview "Price each"
// DISPLAY (NetSuite still auto-prices the written order).
export interface LeveledPriceTier { min_qty: number; unit_price: number }
export interface LeveledPriceSchedule {
  schedule_name: string | null;
  // tier-2 price exists but its quantity breakpoint couldn't be resolved -> show
  // the base tier only and flag (NetSuite auto-prices the real order).
  break_unknown: boolean;
  tiers: LeveledPriceTier[];   // ascending by min_qty
}
export interface LeveledPricesResp {
  price_level: { id: string; name: string | null } | null;
  schedules: Record<string, LeveledPriceSchedule>;   // keyed by item_id (string)
}
// Per-item quantity-break schedule for the customer's price level. Customer id is
// REQUIRED — with no customer we can't price (returns empty). Items with no
// per-level price are omitted (the preview blanks + flags them).
export const getLeveledPrices = async (
  customerId: number | null | undefined, ids: number[]
): Promise<LeveledPricesResp> => {
  const clean = Array.from(new Set(ids.filter(n => Number.isFinite(n))));
  if (!customerId || clean.length === 0) return { price_level: null, schedules: {} };
  const r = await api.get('/netsuite/leveledprices',
    { params: { cid: customerId, ids: clean.join(',') } });
  return r.data;
};

// NetSuite-auto-applied PROMOTION discount lines on an existing SO, each bound to
// the item line it discounts (parent_line/parent_item_*). is_promo distinguishes a
// real promotion (NetSuite sets a per-line memo like "$0.70 off per bag") from a
// rep-added "+ Discount" line (no memo). Read-only / display — NetSuite owns these
// lines, SLX never writes them. Non-fatal: [] when the SO has none / on error.
export interface PromoLine {
  line: number;
  discount_item_id: number | null;
  discount_item_name: string | null;
  amount: number | null;
  memo: string | null;
  is_promo: boolean;
  parent_line: number | null;
  parent_item_id: number | null;
  parent_item_name: string | null;
}
export const getOrderPromoLines = async (soId: number | null | undefined): Promise<PromoLine[]> => {
  if (!soId) return [];
  const r = await api.get('/netsuite/orderpromolines', { params: { soid: soId } });
  return r.data.promo_lines || [];
};

// PRE-CREATE promotion preview: per-line discounts NetSuite WOULD auto-apply to a
// hypothetical order (customer + item lines), evaluated in-memory by the promo_preview
// RESTlet (no save/write). Used by the manual/new-order preview to show read-only promo
// rows under each item. Non-fatal: [] with no customer / no items / on error.
export interface PreviewPromoLine {
  item_id: number | null;
  quantity: number | null;
  discount_amount: number | null;   // positive $ off this line (0 = none)
  promo_name: string | null;
  applied_promo_ids: number[];
}
export const getPreviewPromoLines = async (
  customerId: number | null | undefined,
  items: Array<{ item_id: number; quantity: number }>
): Promise<PreviewPromoLine[]> => {
  if (!customerId || !items || items.length === 0) return [];
  try {
    const r = await api.post('/netsuite/previewpromolines', { customer_id: customerId, items });
    return r.data.promo_lines || [];
  } catch {
    return [];
  }
};

// Read the vendor invoice attached to a thread and return each order line's
// per-unit price (÷0.9 drop-ship rule applied server-side). index echoes back the
// value we send (we send each line's id) so the caller maps prices to its lines.
export interface InvoicePriceMatch {
  index: any;
  invoice_description: string | null;
  invoice_price: number | null;
  sale_price: number | null;
  confidence: number | null;
}
export const fillInvoicePrices = async (
  messageId: number,
  lines: { index: any; name: string }[]
): Promise<{ attachment_found: boolean; pages?: number; divisor?: number;
             matches: InvoicePriceMatch[]; message?: string; error?: string }> => {
  const r = await api.post('/netsuite/invoiceprices', { message_id: messageId, lines });
  return r.data;
};

export interface DropshipVariant {
  ds_item_id: number; ds_itemid: string; ds_name: string;
  vendor_id: string | null; vendor_name: string | null; po_rate: number | null;
}
// The drop-ship counterpart of an ordinary item (+ the vendor a direct-ship line
// POs from). null when the item has no '-DS' variant.
export const getDropshipVariant = async (itemId: number): Promise<DropshipVariant | null> => {
  const r = await api.get('/netsuite/item/' + itemId + '/dropship');
  return r.data.dropship || null;
};

// Per-unit weight (lb) parsed from an item NAME — the SAME token the SO preview
// and the backend truck-fill sizing read ("50#BG" -> 50, "40LB BAG" -> 40). Takes
// the LAST weight token (pack size sits at the end of the name); a formula code
// like "26-18" has no #/lb suffix so it's never mistaken for a weight. Returns
// null when the name carries no weight token. Shared with OrderItemEditor's
// preview display so the shown and the sent weight can never drift.
export const perUnitLbFromName = (name?: string | null): number | null => {
  if (!name) return null;
  const hits = String(name).match(/\d+(?:\.\d+)?\s*(?:#|lbs?)\b/gi);
  if (!hits || hits.length === 0) return null;
  const num = parseFloat(hits[hits.length - 1]);
  return isNaN(num) ? null : num;
};

// Physical order weight (lb): sum of per-unit weight (from the item NAME) x BASE
// quantity across resolved lines. Credit lines (DIS*/marketing) and name-less lines
// carry no weight token -> contribute 0. This is the exact number the SO preview
// shows; we send it so NetSuite's custbody_jjb_order_weight matches the preview.
export const computeOrderWeightLb = (items: MessageItem[]): number => {
  let total = 0;
  items.filter(i => i.item_id).forEach(i => {
    const per = perUnitLbFromName(i.resolved_item);
    const qty = Number(i.quantity) || 0;
    if (per != null) total += per * qty;
  });
  return total;
};

// Sales Order Creation
export const createSalesOrder = async (
  messageId: number,
  customerId: number,
  items: MessageItem[],
  memo?: string,
  hotLoad?: boolean,
  header?: SoHeaderFields,
  orderWeightLb?: number,
  previewGroup?: number,
): Promise<any> => {
  const soItems = toSoLinePayload(items);
  // Prefer the preview's NetSuite-item-weight total; the backend recomputes
  // authoritatively regardless, so this is a resilient hint. Fall back to the
  // legacy name-parsed number only when nothing was supplied.
  const weight = orderWeightLb != null ? orderWeightLb : computeOrderWeightLb(items);
  // PROMOTIONS (human-gated). The auto-apply checkbox rides in header; hoist it to a
  // top-level create field. Manual promotion LINES aren't SO item lines — they carry a
  // promotion_id and become the list of promotions to apply.
  const autoApplyPromotions = !!(header && (header as any).autoApplyPromotions);
  const manualPromotionIds = Array.from(new Set(
    (items as any[])
      .filter(i => i.line_kind === 'PROMOTION' && i.promotion_id != null && !i.removed)
      .map(i => Number(i.promotion_id))
      .filter(n => Number.isFinite(n))));
  // autoApplyPromotions is hoisted to a top-level create field; strip it from the
  // nested header so it isn't sent as an (unknown) NetSuite body/custom field.
  const { autoApplyPromotions: _aap, ...headerForSend } = (header || {}) as any;
  const response = await api.post('/netsuite/salesorder', {
    customer_id: customerId,
    items: soItems,
    memo: memo,
    message_id: messageId,
    hot_load: !!hotLoad,
    auto_apply_promotions: autoApplyPromotions,
    manual_promotion_ids: manualPromotionIds,
    // Which preview TAB is being created (multi-preview). The backend scopes its
    // base-unit conversion gate to THIS group's lines so a sibling tab's unresolved
    // line can't convert this order's lines or block its create. Omitted => group 1.
    ...(previewGroup != null ? { preview_group: previewGroup } : {}),
    // SO-preview editable header fields (shipping details, terms & rep, ship-to
    // address, J&J logistics flags) → NetSuite SO body fields.
    ...(header ? { header: headerForSend } : {}),
    // The SO-preview total weight (lb). Omitted when 0 so we never overwrite with
    // a bogus weight (the backend also recomputes from NetSuite item weights).
    ...(weight > 0 ? { order_weight_lb: Math.round(weight) } : {})
  });
  return response.data;
};

// RMA (Return Authorization) creation — the return counterpart of createSalesOrder.
// Sends each resolved return line's item, quantity, and return_rate (the credit
// price). The original SO id (provenance from the preview) links the RA in NetSuite.
// WRITE-GATED server-side: returns 403 {readonly:true} until go-live.
export const createRMA = async (messageId: number, customerId: number, items: MessageItem[], memo?: string): Promise<any> => {
  const rmaItems = items.filter(i => i.item_id).map(i => ({
    item_id: i.item_id,
    quantity: i.quantity || 1,
    rate: i.return_rate ?? null,
    // NetSuite 'Reason for Return' list value id (rep-picked). NetSuite stores one
    // reason per RA (body field); the backend uses the first line that carries one.
    return_reason: i.return_reason ?? null
  }));
  // Original SO / Invoice tie for the RA link — take the first line that carries one.
  const originalSoId = items.find(i => i.original_so_id)?.original_so_id ?? null;
  const originalInvoiceId = items.find(i => i.original_invoice_id)?.original_invoice_id ?? null;
  const originalInvoiceTranid = items.find(i => i.original_invoice_tranid)?.original_invoice_tranid ?? null;
  const response = await api.post('/netsuite/returnauthorization', {
    customer_id: customerId,
    items: rmaItems,
    memo: memo,
    message_id: messageId,
    original_so_id: originalSoId,
    original_invoice_id: originalInvoiceId,
    original_invoice_tranid: originalInvoiceTranid
  });
  return response.data;
};

// Combined recent Sales Orders + Invoices for the RMA "tie to SO / Invoice" picker
// (the source of the delivered price). NON-FATAL: [] on failure.
export interface RmaSource {
  type: 'salesorder' | 'invoice';
  id: number | string;
  tranid: string;
  date?: string | null;
  status?: string | null;
  total?: number | string | null;
}
export const getCustomerSources = async (customerId: number): Promise<RmaSource[]> => {
  try {
    const response = await api.get('/netsuite/customer/' + customerId + '/sources');
    return response.data.sources || [];
  } catch (e) {
    return [];
  }
};

// Delivered per-unit price (base rate + per-unit freight = what the customer was
// BILLED) for the items on a tied SO / Invoice, keyed by item id (as string).
// NON-FATAL: empty map on failure.
export interface DeliveredPrice {
  base_rate: number | null;
  freight_per_unit: number | null;
  delivered_rate: number | null;
  unit_weight: number | null;
  freight_source: string;
  has_promo: boolean;
}
export const getDeliveredPrices = async (
  sourceType: string, sourceId: number | string
): Promise<{ prices: Record<string, DeliveredPrice>; has_promo: boolean }> => {
  try {
    const response = await api.get('/netsuite/deliveredprices', {
      params: { source_type: sourceType, source_id: sourceId }
    });
    return { prices: response.data.prices || {}, has_promo: !!response.data.has_promo };
  } catch (e) {
    return { prices: {}, has_promo: false };
  }
};

// The active NetSuite 'Reason for Return' options for the RMA preview's reason
// dropdown. [{id, name}], data-driven (a new NetSuite reason appears automatically).
export const getReturnReasons = async (): Promise<{ id: string; name: string }[]> => {
  const response = await api.get('/netsuite/returnreasons');
  return response.data.reasons || [];
};

// ── Credit Memo (CM) preview ─────────────────────────────────────────────────
// The credit-desk counterpart of the SO/RMA preview: Kelsey ties in an existing
// invoice (reference-only), applies a DIS-item credit line at a value, and creates
// a STANDALONE Credit Memo in NetSuite (write-gated, human-clicked).

// The thread's CREDIT-MEMO lines only (kind=CM) — same thread sweep as
// getThreadOrderItems, filtered server-side to line_kind='CM'.
export const getThreadCmItems = async (messageId: number, group?: number): Promise<MessageItem[]> => {
  const params: any = { scope: 'thread', kind: 'CM' };
  if (group != null) params.group = group;
  const response = await api.get('/messages/' + messageId + '/items', { params });
  return response.data;
};

// Is this thread a CREDIT thread (any inbound message flagged is_credit_request)?
// Backs the CM-preview auto-mount. NON-FATAL: false on any error.
export const getThreadIsCredit = async (threadRootId: number): Promise<boolean> => {
  try {
    const r = await api.get('/netsuite/threadcredit/' + threadRootId);
    return !!(r.data && r.data.is_credit);
  } catch (e) {
    return false;
  }
};

// A customer's invoices for the CM preview's "tie in an invoice" dropdown
// (reference-only). [{id, tranid, trandate, status, total}]. NON-FATAL ([] on error).
export interface CustomerInvoice {
  id: number; tranid: string; trandate?: string | null;
  status?: string | null; total?: number | null;
}
export const getCustomerInvoices = async (customerId: number): Promise<CustomerInvoice[]> => {
  try {
    const response = await api.get('/netsuite/customer/' + customerId + '/invoices');
    return response.data.invoices || [];
  } catch (e) {
    return [];
  }
};

// The editable Credit-Memo-preview HEADER (the fields Kelsey fills on the CM form).
export interface CmHeaderFields {
  date?: string | null;            // YYYY-MM-DD (tranDate)
  location_id?: string | null;
  sales_rep_id?: string | null;
  department_id?: string | null;
  class_id?: string | null;
  discount_item_id?: string | null;  // header-level DISCOUNT item
  discount_rate?: number | null;      // header-level discount rate
  po_number?: string | null;          // otherRefNum
  custom_form_id?: string | null;
  // Tied-in invoice (reference-only; recorded in the CM memo, not applied).
  invoice_id?: number | null;
  invoice_tranid?: string | null;
}

// Persisted CM-preview header (date/location/rep/dept/class/discount/PO# + the
// invoice reference), keyed by the credit message id, so close/reopen doesn't lose
// Kelsey's edits. Opaque JSON. Mirrors get/saveSoPreviewPrefs (separate table).
export const getCmPreviewPrefs = async (messageId: number, groupNo: number = 1): Promise<any> => {
  const r = await api.get('/netsuite/cmpreviewprefs/' + messageId, { params: { group: groupNo } });
  return (r.data && r.data.prefs) || {};
};
export const saveCmPreviewPrefs = async (messageId: number, prefs: any, groupNo: number = 1): Promise<void> => {
  await api.put('/netsuite/cmpreviewprefs/' + messageId, { prefs, group: groupNo });
};

// Create a standalone Credit Memo from the CM preview. Sends each resolved DIS-item
// credit line (item + credit amount) + the parity header + the referenced invoice
// number (folded into the CM memo). HARD customer guard server-side (400 without a
// customer). WRITE-GATED (create_credit_memo -> _gate_write); go-live is executed,
// so this creates a REAL credit memo in production.
export const createCreditMemo = async (
  messageId: number,
  customerId: number,
  items: MessageItem[],
  memo?: string,
  header?: CmHeaderFields,
): Promise<any> => {
  const cmItems = items
    .filter(i => i.item_id)
    .map(i => ({
      item_id: i.item_id,
      quantity: 1,
      // The credit unit amount = the DIS-line credit value (stored POSITIVE; the
      // credit-memo transaction type carries the sign).
      rate: i.credit_amount != null ? Math.abs(Number(i.credit_amount) || 0) : null,
      // Per-line DEPARTMENT (NetSuite department internal id) — the CM line's
      // DEPARTMENT column; omitted server-side when unset.
      department_id: (i as any).line_department_id ?? null,
    }));
  const response = await api.post('/netsuite/creditmemo', {
    customer_id: customerId,
    items: cmItems,
    memo: memo,
    message_id: messageId,
    ...(header ? { header } : {}),
    // The tied-in invoice number (reference-only) — folded into the CM memo server-side.
    ...(header?.invoice_tranid ? { invoice_tranid: header.invoice_tranid } : {}),
  });
  return response.data;
};

// Order Edit (ORDER-EDIT-PLAN-v2): list a customer's editable sales orders,
// fetch one order's lines for prefill, and submit line changes. All go through
// the axios `api` client so the MSAL bearer + 401/403 interceptor apply.
export const getEditableOrders = async (customerId: number): Promise<any[]> => {
  const response = await api.get('/netsuite/customer/' + customerId + '/orders', { params: { editable: 1 } });
  return response.data.orders || [];
};

export const getSalesOrderLines = async (soId: number): Promise<any[]> => {
  const response = await api.get('/netsuite/salesorder/' + soId + '/lines');
  return response.data.lines || [];
};

// The order's CURRENT header (ship method / date / terms / rep / addresses) in the
// SO-preview field shape, for seeding an EDIT preview so it shows real current
// values and the Revert button can reset to exactly this. Read-only. Returns {}
// on any problem (the editor just falls back to blank header fields).
export type SoHeaderSeed = SoHeaderFields & { ship_method_name?: string | null };
export const getSalesOrderHeader = async (soId: number): Promise<SoHeaderSeed> => {
  try {
    const response = await api.get('/netsuite/salesorder/' + soId + '/header');
    return (response.data && response.data.header) || {};
  } catch (e) {
    return {};
  }
};

// PATCH the order's lines. 409 (locked) / 400 (empty) are NOT swallowed here —
// they reject so the caller can surface the right message to the rep.
export const submitOrderChanges = async (
  soId: number,
  items: MessageItem[],
  memo?: string,
  hotLoad?: boolean,
  header?: SoHeaderFields,
  orderWeightLb?: number,
  messageId?: number,
  previewGroup?: number,
): Promise<any> => {
  // Same credit-line handling as createSalesOrder: DISCOUNT/MARKETING lines ride
  // along as negative-rate credit lines; normal lines auto-price.
  const body: any = { items: toSoLinePayload(items), memo };
  // The thread + preview TAB this edit came from, so the backend can record it and
  // auto-complete the thread exactly like a create (a "change my order" thread
  // used to sit in New after the edit was submitted).
  if (messageId != null) body.message_id = messageId;
  if (previewGroup != null) body.preview_group = previewGroup;
  if (hotLoad !== undefined) body.hot_load = !!hotLoad;
  if (header) body.header = header;
  // A line edit changes the order's weight — send it so custbody_jjb_order_weight
  // stays in sync (the backend also recomputes from NetSuite item weights).
  const weight = orderWeightLb != null ? orderWeightLb : computeOrderWeightLb(items);
  if (weight > 0) body.order_weight_lb = Math.round(weight);
  const response = await api.patch('/netsuite/salesorder/' + soId, body);
  return response.data;
};

// Pending order edits (AI-prepared existing-order edits, feature #4 Option A).
// The AI stages proposed line changes for an existing SO; the frontend loads
// them per-thread, overlays them on the SO's current lines for human review,
// and resolves (applied/discarded) after the human submits or dismisses. Both
// go through the axios `api` client so the bearer + 401/403 interceptor apply.
export const getPendingOrderEdits = async (threadId: number): Promise<any[]> => {
  const response = await api.get('/pending-order-edits', { params: { threadId } });
  return response.data.pending || [];
};

export const resolvePendingOrderEdit = async (id: number, status: string): Promise<any> => {
  const response = await api.post('/pending-order-edits/' + id, { status });
  return response.data;
};

// Persist a rep's MANUAL line edits on a pulled-in existing order as a pending
// order edit (edit_origin='manual'), so they survive close/reopen and Sally
// respects them. Upserts the thread's open pending row for this SO; an EMPTY
// ops list (rep reverted to the SO's current lines) discards it. `ops` are
// {op,item_id,shorthand,quantity} computed against the SO's PRISTINE lines.
// NON-FATAL for the caller (persistence must never block editing).
export const saveManualOrderEdit = async (
  threadId: number,
  payload: {
    netsuite_so_id: number;
    so_tranid?: string | null;
    message_id?: number | null;
    customer_id?: number | null;
    proposed_edits: Array<{ op: string; item_id: number | null; shorthand?: string | null; quantity?: number | null }>;
  }
): Promise<any> => {
  const response = await api.post('/pending-order-edits', { threadId, ...payload });
  return response.data;
};

// Thread edit target (Part B): persist the rep's manual "Edit existing order"
// dropdown pick so it survives a reload AND Prompt AI corrects THAT order instead
// of building a new-order preview. so_id=null clears the lock ("New order"). The
// order-level analogue of the thread customer-lock. Both go through the axios
// `api` client so the MSAL bearer + 401/403 interceptor apply. NON-FATAL by design
// on the server; callers here also swallow failures (the pick still works locally).
export const setThreadEditTarget = async (
  threadId: number,
  soId: number | null,
  tranid?: string | null
): Promise<any> => {
  const response = await api.post('/threads/' + threadId + '/edit-target', {
    so_id: soId,
    tranid: tranid ?? null,
  });
  return response.data;
};

export const getThreadEditTarget = async (threadId: number): Promise<any | null> => {
  const response = await api.get('/threads/' + threadId + '/edit-target');
  return response.data?.target ?? null;
};

// Cancel (close) an existing NetSuite sales order. This is the ONLY cancel WRITE
// and is always HUMAN-clicked. Mirrors submitOrderChanges' surfacing: 409 (the
// order is no longer editable / not Pending-Approval) and 400 are NOT swallowed
// here — they reject so the caller can show the right message to the rep. Goes
// through the axios `api` client so the MSAL bearer + 401/403 interceptor apply.
export const cancelSalesOrder = async (soId: number): Promise<any> => {
  const response = await api.post('/netsuite/salesorder/' + soId + '/close');
  return response.data;
};

// NetSuite Item Search
export const searchNetSuiteItems = async (query: string): Promise<any[]> => {
  const response = await api.get('/netsuite/items', { params: { search: query, limit: 10 } });
  return response.data.items || [];
};

// Customer type-ahead for the employee-thread target-customer dropdown (#7).
// Searches NetSuite customers by free text. NON-FATAL for the caller: a failed
// or short-query search returns [] rather than throwing, so the picker degrades
// gracefully (the 401/403 interceptor still toasts auth failures). The backend
// already returns [] for empty/short q, but we guard the trimmed length too so
// we don't fire a request on every keystroke before there's anything to match.
export const searchCustomers = async (q: string): Promise<{ id: number; name: string }[]> => {
  try {
    const term = (q || '').trim();
    if (term.length < 2) return [];
    const response = await api.get('/netsuite/customers', { params: { q: term, limit: 10 } });
    return response.data?.customers || [];
  } catch (e) {
    /* non-fatal: a type-ahead search must never disrupt the UI */
    return [];
  }
};

// Set (confirm/override) the resolved TARGET customer for an employee-originated
// message (#7). Also resolves any pending "which customer?" ask server-side.
// Unlike searchCustomers, errors are SURFACED (re-thrown) so the calling UI can
// react (e.g. show a failure and not optimistically mark it confirmed).
export const setMessageTargetCustomer = async (
  messageId: number,
  customerId: number,
  customerName: string
): Promise<any> => {
  const response = await api.post('/messages/' + messageId + '/target-customer', {
    customer_id: customerId,
    customer_name: customerName
  });
  return response.data;
};

// "Ask next step" — FORCE the AI to read a thread and draft the next pending
// question (customer -> item -> quantity), bypassing the human-presence
// suppression because the rep explicitly asked for it. id may be any message id
// in the thread OR the thread root. Errors are SURFACED so the UI can react.
export const advanceThread = async (threadId: number): Promise<any> => {
  const response = await api.post('/threads/' + threadId + '/advance');
  return response.data;
};

// Submit a correction (teaches the shorthand dictionary)
export const submitItemCorrection = async (
  messageId: number,
  data: {
    item_id?: number;
    shorthand: string;
    corrected_item_id: number;
    corrected_item_name: string;
    corrected_quantity?: number;
    customer_id?: number;
  }
): Promise<any> => {
  const response = await api.post('/messages/' + messageId + '/items', data);
  return response.data;
};

// SO-preview v2: persist a rep's edited WANTED qty+unit on one order line and
// get back the authoritative BASE (server reconverts via the shared conversion
// choke point). Body carries the wanted pair; item_id optional (re-resolves the
// line's conversion factor). Response: {success, line_id, entered_qty,
// entered_unit, quantity, unit, conversion_display, used_default, needs_review}.
// Through the shared axios `api` client (MSAL bearer + 401/403 interceptor).
export const updateOrderLine = async (
  messageId: number,
  body: { line_id: number; entered_qty: number; entered_unit: string; item_id?: number }
): Promise<any> => {
  const response = await api.patch('/messages/' + messageId + '/items', body);
  return response.data;
};

// RMA preview: persist a rep's edited return price and/or reason on one return
// line. Same PATCH endpoint; a return_* key routes to the return-fields path,
// which sets user_corrected=1 so the AI never overwrites the human's value.
export const updateRmaLine = async (
  messageId: number,
  body: {
    line_id: number;
    return_rate?: number | null;
    return_reason?: string | null;
    original_so_id?: number | null;
    original_so_tranid?: string | null;
    original_invoice_id?: number | null;
    original_invoice_tranid?: string | null;
    return_freight_per_unit?: number | null;
    return_source_has_promo?: boolean | null;
  }
): Promise<any> => {
  const response = await api.patch('/messages/' + messageId + '/items', body);
  return response.data;
};

// The DIS* item family (SKU starts with 'DIS') — the option source for the SO
// preview's discount dropdown AND the per-employee marketing-funds match. Returns
// [{id, itemid, displayname}]. NON-FATAL for the caller: [] on failure so the
// dropdown degrades to empty rather than throwing.
export const getDiscountItems = async (): Promise<{ id: string; itemid: string; displayname: string }[]> => {
  try {
    const response = await api.get('/netsuite/discountitems');
    return response.data.items || [];
  } catch (e) {
    return [];
  }
};

// The per-employee 'Marketing Funds' items (e.g. "TYLER FORD - Marketing Funds")
// for the SO preview's "Add Marketing Line" dropdown — distinct from the DIS*
// discount items. Same shape; NON-FATAL ([] on failure).
export const getMarketingItems = async (): Promise<{ id: string; itemid: string; displayname: string }[]> => {
  try {
    const response = await api.get('/netsuite/marketingitems');
    return response.data.items || [];
  } catch (e) {
    return [];
  }
};

// Add a DISCOUNT (under a parent order line) or MARKETING (top-level, employee
// threads) credit line to a message's SO preview. Returns the new line's id so the
// editor can key the row on it. Persisted like RMA lines; the AI grounding never
// touches these rows.
export const addPreviewLine = async (
  messageId: number,
  body: {
    // 'SO' persists a rep-added ORDER line (once resolved) so it behaves exactly
    // like an auto-parsed line; 'CM' persists a Credit-Memo DIS-item credit line;
    // the credit/RMA kinds are unchanged.
    line_kind: 'SO' | 'DISCOUNT' | 'MARKETING' | 'RMA' | 'CM';
    item_id?: number | null;
    resolved_item?: string | null;
    credit_amount?: number | null;
    parent_line_id?: number | null;
    // RMA add-line: which Return Authorization the new return item belongs to.
    rma_group?: number | null;
    // SO/CM add-line: which PREVIEW TAB the new order/credit line belongs to.
    preview_group?: number | null;
    // CM add-line: the picked NetSuite department internal id for the credit line.
    department_id?: string | null;
    shorthand?: string | null;
    quantity?: number | null;
    unit?: string | null;
    resolution_confidence?: number | null;
  }
): Promise<any> => {
  const response = await api.post('/messages/' + messageId + '/items', { op: 'add_line', ...body });
  return response.data;
};

// Persist a rep's edit to a credit line's discount item, credit amount, and/or the
// per-line department. Pass department_id: '' to CLEAR the department (-> NULL);
// omit the key to leave it untouched.
export const updatePreviewCredit = async (
  messageId: number,
  body: { line_id: number; item_id?: number | null; resolved_item?: string | null; credit_amount?: number | null; department_id?: string | null }
): Promise<any> => {
  const response = await api.patch('/messages/' + messageId + '/items', { op: 'update_credit', ...body });
  return response.data;
};

// Hard-delete a persisted credit line (the rep removed a discount/marketing line).
export const deletePreviewLine = async (messageId: number, lineId: number): Promise<any> => {
  const response = await api.post('/messages/' + messageId + '/items', { op: 'delete_line', line_id: lineId });
  return response.data;
};

// Stamp the resolved item on a line WITHOUT learning a shorthand mapping — used when
// a rep resolves a HAND-ADDED return line whose shorthand is a neutral placeholder
// (the normal correction path would pollute the learned/NetSuite shorthand dict).
export const setPreviewLineItem = async (
  messageId: number,
  body: { line_id: number; item_id: number | null; resolved_item?: string | null }
): Promise<any> => {
  const response = await api.post('/messages/' + messageId + '/items', { op: 'set_line_item', ...body });
  return response.data;
};

// Customer 360: pull what the backend already knows from NetSuite.
export const getNsCustomer = async (id: number): Promise<any> => {
  const response = await api.get('/netsuite/customer/' + id);
  return response.data;
};

export const getCustomerHistory = async (id: number, months = 12): Promise<any[]> => {
  const response = await api.get('/netsuite/customer/' + id + '/history', { params: { months } });
  return response.data.items || [];
};

// Create a NetSuite customer from an unknown sender and link it to their
// messages so the conversation resolves.
export const createNsCustomer = async (payload: {
  sender_id: string;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  is_person?: boolean;
}): Promise<any> => {
  const response = await api.post('/netsuite/customer', payload);
  return response.data;
};

// Assignable-user directory for the manager "Assign to..." picker. Each entry
// carries a stable email (the assignee key) plus a human-readable name (label).
//
// CACHING RULE: cache ONLY a successful, non-empty result. An empty/failed load
// (e.g. the directory fetch raced component mount and went out before the MSAL
// bearer token was set -> 401/403 -> []) must NOT be cached, or the picker would
// stay empty for the whole session with no retry. Leaving the cache null lets the
// next open() retry once auth is established.
let usersCache: { name: string; email: string }[] | null = null;
let usersInflight: Promise<{ name: string; email: string }[]> | null = null;

// Last directory-load diagnostic, surfaced in the picker's empty state so a
// failure is visible on-screen (no DevTools needed). e.g. "graph=0, history 403".
export let lastUsersDiag = '';

export const getUsers = async (): Promise<{ name: string; email: string }[]> => {
  if (usersCache && usersCache.length) return usersCache;
  if (usersInflight) return usersInflight;
  usersInflight = (async () => {
    let diag = '';
    try {
      // Prefer the Azure AD org directory (authoritative, clean, deduped). Fall
      // back to the history-derived list (people seen in threads, scrubbed of
      // system labels server-side) when Graph is unavailable / not consented.
      // getOrgUsers() returns [] on any failure, so the fallback is automatic.
      const org = await getOrgUsers();
      if (org && org.length) {
        // Limit to people who hold an app role (SALES/MANAGER/ADMIN) — it makes
        // no sense to assign/view-as someone with no role. roleIds is null when
        // the role lookup is unavailable (Directory.Read.All not consented yet);
        // in that case (or if nothing matched) we show the full org rather than
        // an empty list, so the picker still works before consent is granted.
        const roleIds = await getRoleAssignedUserIds();
        if (roleIds && roleIds.size) {
          const roled = org.filter((u) => u.id && roleIds.has(u.id)).map((u) => ({ name: u.name, email: u.email }));
          if (roled.length) {
            lastUsersDiag = `graph=${org.length}, role-holders=${roled.length}`;
            console.log('[getUsers]', lastUsersDiag);
            usersCache = roled;
            return roled;
          }
          lastUsersDiag = `graph=${org.length}, roles=${roleIds.size}, matched=0 (showing all)`;
        } else {
          lastUsersDiag = `graph=${org.length} (role filter unavailable — grant Directory.Read.All)`;
        }
        console.log('[getUsers]', lastUsersDiag);
        const all = org.map((u) => ({ name: u.name, email: u.email }));
        usersCache = all;
        return all;
      }
      diag = `graph=0`;
      try {
        const r = await api.get('/users');
        const history = (r.data.users || []) as { name: string; email: string }[];
        diag += `, history=${history.length}`;
        console.log('[getUsers]', diag);
        lastUsersDiag = diag;
        if (history.length) { usersCache = history; }
        return history;
      } catch (e: any) {
        const status = e?.response?.status;
        diag += `, history ${status ? 'HTTP ' + status : 'error'}`;
        console.warn('[getUsers]', diag, e);
        lastUsersDiag = diag;
        return [];
      }
    } catch (e: any) {
      lastUsersDiag = (diag ? diag + ', ' : '') + 'error';
      console.warn('[getUsers] error', e);
      return [];
    } finally {
      usersInflight = null;
    }
  })();
  return usersInflight;
};

// UI policy flags (e.g. notification routing). Defaults to manager-dispatch.
export const getSettings = async (): Promise<{ newMessageNotify: 'managers' | 'all' }> => {
  const response = await api.get('/settings');
  return response.data;
};

// Draft-and-approve: AI-proposed replies a human reviews before sending.
export interface AiDraft {
  id: number;
  message_id: number;
  thread_id: number | null;
  channel: string;
  recipient: string;
  draft_text: string;
  // A rep's in-progress edit, persisted (D2) so it survives close/reopen; the
  // editor seeds from edited_text ?? draft_text. Null until they edit.
  edited_text?: string | null;
  confidence: number | null;
  status: string;
  created_at: string;
}

export const getDrafts = async (threadId?: number): Promise<AiDraft[]> => {
  const response = await api.get('/drafts', { params: threadId != null ? { threadId } : {} });
  return response.data || [];
};

export const sendDraft = async (id: number, text?: string): Promise<any> => {
  const response = await api.post('/drafts/' + id, { action: 'send', text });
  return response.data;
};

export const discardDraft = async (id: number): Promise<any> => {
  const response = await api.post('/drafts/' + id, { action: 'discard' });
  return response.data;
};

// D2: persist an in-progress edit to a pending draft (debounced by the caller)
// so it survives close/reopen. Stored server-side in edited_text (not draft_text).
export const saveDraftText = async (id: number, text: string): Promise<any> => {
  const response = await api.post('/drafts/' + id, { action: 'save', text });
  return response.data;
};

// API Interaction Tracking (admin-only observability). One row per outbound/inbound
// integration call (netsuite / graph / messagemedia / claude / internal). Backend
// enforces the ADMIN role; the frontend gate is UX-only. Mirrors getDrafts: simple
// typed get fns through the axios `api` client so the MSAL bearer + 401/403
// interceptor apply.
export interface ApiInteractionRow {
  id: number;
  ts: string;
  direction: string;
  service: string;
  method: string | null;
  endpoint: string | null;
  status_code: number | null;
  ok: boolean;
  latency_ms: number | null;
  error: string | null;
  request_summary: string | null;
  response_summary: string | null;
  message_id: number | null;
  thread_id: number | null;
  principal: string | null;
}

// Keyset-paged list: pass before_id (the returned next_before_id) to load older.
// next_before_id is null when there are no older rows.
export interface ApiInteractionsPage {
  rows: ApiInteractionRow[];
  next_before_id: number | null;
}

export const getApiInteractions = async (params: {
  limit?: number;
  service?: string;
  ok?: boolean;
  before_id?: number;
}): Promise<ApiInteractionsPage> => {
  const response = await api.get('/api-interactions', { params });
  // Backend nests the list under `interactions` (ai_decisions_api convention);
  // remap to the component's `rows` contract here so the blast radius is one file.
  return {
    rows: response.data.interactions ?? [],
    next_before_id: response.data.next_before_id,
  };
};

// Aggregate counts/latency over the last `days` window, grouped by service plus a
// per-day series, for the dashboard's SUMMARY panel. Shape is the FROZEN summary
// contract owned jointly with the backend (api-interactions/summary). Mirrors
// getApiInteractions: a typed get through the axios `api` client so the MSAL
// bearer + 401/403 interceptor apply.
export interface ApiInteractionsSummaryTotals {
  total: number;
  inbound: number;
  outbound: number;
  ok: number;
  failed: number;
  success_rate: number;
}

export interface ApiInteractionsSummaryService {
  service: string;
  count: number;
  ok: number;
  failed: number;
  success_rate: number;
  avg_latency_ms: number | null;
  max_latency_ms: number | null;
  last_ts: string | null;
  last_failure_ts: string | null;
}

export interface ApiInteractionsSummaryDay {
  day: string;
  count: number;
  failed: number;
}

export interface ApiInteractionsSummary {
  days: number;
  totals: ApiInteractionsSummaryTotals;
  by_service: ApiInteractionsSummaryService[];
  by_day: ApiInteractionsSummaryDay[];
}

// Empty totals fallback so the component can render KPI cards even if the backend
// omits a field; keeps the panel from throwing on a partial/malformed response.
const EMPTY_SUMMARY_TOTALS: ApiInteractionsSummaryTotals = {
  total: 0, inbound: 0, outbound: 0, ok: 0, failed: 0, success_rate: 0,
};

export const getApiInteractionsSummary = async (days?: number): Promise<ApiInteractionsSummary> => {
  const response = await api.get('/api-interactions/summary', { params: { days } });
  const d = response.data || {};
  // Guard each branch of the contract so a partial response degrades gracefully
  // (the panel shows zeros/empties) rather than crashing the dashboard.
  return {
    days: typeof d.days === 'number' ? d.days : (days ?? 0),
    totals: { ...EMPTY_SUMMARY_TOTALS, ...(d.totals || {}) },
    by_service: Array.isArray(d.by_service) ? d.by_service : [],
    by_day: Array.isArray(d.by_day) ? d.by_day : [],
  };
};

// Self-building Q&A knowledge base. When the AI hits a customer question it can't
// answer from the catalog/history, it logs an "open question" against the thread.
// A rep answers it in-thread; the answer is promoted to a reusable KB entry that
// grounds future AI replies. Mirrors getDrafts: typed calls through the shared
// axios `api` client so the MSAL bearer + 401/403 interceptor apply. Paths use the
// leading-slash convention (`/qa-questions`), same as getDrafts (`/drafts`).
export interface QaOpenQuestion {
  id: number;
  message_id: number;
  thread_id: number | null;
  customer_id: number | null;
  question_text: string;
  category: string | null;
  status: string;
  created_at: string;
}

export interface QaKbEntry {
  id: number;
  question_text: string;
  answer_text: string;
  category: string | null;
  scope: string;
  customer_id: number | null;
  status: string;
  reuse_count: number;
  last_used_at: string | null;
  created_at: string;
  // Joined customer identity columns (present when scope === 'customer'; the
  // backend left-joins the customer, so these are null for general entries).
  customer_name?: string | null;
  customer_entityid?: string | null;
  customer_email?: string | null;
}

// threadId omitted -> the FULL open-questions queue (backend get_open_qa_questions
// treats a missing threadId as "all threads"). The per-thread OpenQuestion card
// passes a threadId; the central AI Info queue calls it with none.
export const getOpenQuestions = async (threadId?: number): Promise<QaOpenQuestion[]> => {
  const response = await api.get('/qa-questions', {
    params: threadId != null ? { threadId } : {},
  });
  return response.data || [];
};

export const answerQuestion = async (
  id: number,
  answerText: string,
  scope: 'general' | 'customer'
): Promise<any> => {
  const response = await api.post('/qa-questions/' + id, {
    action: 'answer',
    answer_text: answerText,
    scope,
  });
  return response.data;
};

export const dismissQuestion = async (id: number): Promise<any> => {
  const response = await api.post('/qa-questions/' + id, { action: 'dismiss' });
  return response.data;
};

// KB admin (used by an optional management screen). Keyset-paged list mirroring
// getApiInteractions: pass before_id (the returned next_before_id) to load older.
export interface QaKbPage {
  entries: QaKbEntry[];
  next_before_id: number | null;
  count: number;
}

export const getKbEntries = async (params: {
  limit?: number;
  q?: string;
  identityQuery?: string;
  scope?: string;
  category?: string;
  status?: string;
  customerId?: number;
  before_id?: number;
}): Promise<QaKbPage> => {
  const response = await api.get('/qa-kb', { params });
  const d = response.data || {};
  return {
    entries: Array.isArray(d.entries) ? d.entries : [],
    next_before_id: d.next_before_id ?? null,
    count: typeof d.count === 'number' ? d.count : (Array.isArray(d.entries) ? d.entries.length : 0),
  };
};

export const editKbEntry = async (
  id: number,
  body: { question_text?: string; answer_text?: string; category?: string | null; scope?: string; status?: string }
): Promise<any> => {
  const response = await api.post('/qa-kb/' + id, { action: 'edit', ...body });
  return response.data;
};

export const archiveKbEntry = async (id: number): Promise<any> => {
  const response = await api.post('/qa-kb/' + id, { action: 'archive' });
  return response.data;
};

// Agentic inbound "brain" — READ-ONLY decision/reasoning trace. The brain logs
// its decisions into the SAME ai_decision_log table the ai-decisions endpoint
// reads (GET /ai-decisions?limit=&channel=&before_id=), with action in
// {'agent_shadow','agent_draft','agent_escalate','agent_delegate_order'} and a
// context_json carrying {agent_trace:[{tool,input_summary,ok,result_summary}],
// would_draft, mode, iterations, confidence}. There is NO existing ai-decisions
// client to extend, so we add a thin one here and CLIENT-SIDE filter to the
// agent_* rows. context_json is left as a raw string and parsed defensively in
// the component (the backend stores it verbatim). Mirrors getApiInteractions /
// getDrafts: typed get fns through the shared axios `api` instance so the MSAL
// bearer + 401/403 interceptor apply; leading-slash path convention.
export interface AgentDecision {
  id: number;
  message_id: number | null;
  thread_id: number | null;
  channel: string | null;
  sender_id: string | null;
  customer_id: number | null;
  customer_name: string | null;
  intent: string | null;
  confidence: number | null;
  action: string;
  route: string | null;
  escalated: boolean | null;
  needs_review: boolean | null;
  draft_text: string | null;
  model: string | null;
  est_cost_usd: number | null;
  latency_ms: number | null;
  // Raw JSON text (parsed defensively in the component). The brain stashes its
  // reasoning trace + mode/iterations/would_draft here.
  context_json: string | null;
  items_json: string | null;
  outcome: string | null;
  final_text: string | null;
  edited: boolean | null;
  outcome_by: string | null;
  outcome_at: string | null;
  created_at: string;
}

export interface AgentDecisionsPage {
  decisions: AgentDecision[];
  next_before_id: number | null;
  count: number;
}

// True when a decision row came from the agentic brain (action starts with
// `agent_`). Used to client-side filter the shared ai-decisions feed down to the
// brain's own decisions.
export const isAgentAction = (action?: string | null): boolean =>
  typeof action === 'string' && action.startsWith('agent_');

export const getAgentDecisions = async (params?: {
  limit?: number;
  before_id?: number;
}): Promise<AgentDecisionsPage> => {
  const response = await api.get('/ai-decisions', { params: params || {} });
  const d = response.data || {};
  const all: AgentDecision[] = Array.isArray(d.decisions) ? d.decisions : [];
  // CLIENT-SIDE filter to the brain's rows (action starts with `agent_`). The
  // endpoint mixes all decision rows; the trace view only audits agent_* ones.
  const decisions = all.filter((row) => isAgentAction(row?.action));
  return {
    decisions,
    next_before_id: d.next_before_id ?? null,
    // count reflects the filtered (agent_*) rows actually returned to the caller.
    count: decisions.length,
  };
};

// 8x8 call-transcript DETAIL (Phase 3, plan 8x8-transcripts-feed-v3.0). Calls
// now live in the UNIFIED feed as channel='CALL' message rows; this is the one
// surviving transcript endpoint — the bubble source for a CALL thread. The id
// is the card's external_id with the 'call:' prefix stripped. (The Phase 2
// list GET + reviewed POST endpoints were removed with the tab UI.) Typed call
// through the shared axios `api` instance so the MSAL bearer + 401/403
// interceptor apply.
export const getCallTranscript = async (id: number): Promise<CallTranscript> => {
  const response = await api.get('/calltranscripts/' + id);
  return response.data;
};

// ---------------------------------------------------------------------------
// AI Info — Customer 360 (AIINFO-v2.0). One aggregator call returns everything
// the AI has learned / can derive for a customer: identity, per-customer Q&A,
// nicknames, shorthand overrides (local editable + NetSuite read-only), recent
// items (derived live from NetSuite, read-only), pallet counts (override / net-
// suite / default, source-labeled) and the corrections log (read-only). Each
// section can fail independently — the backend reports per-section errors under
// `errors` so a partial failure still renders the healthy sections. Mirrors the
// other typed gets: through the shared axios `api` client so the MSAL bearer +
// 401/403 interceptor apply; leading-slash path convention.
export const getAiInfoCustomer = async (id: number): Promise<AiInfoCustomer> => {
  const response = await api.get('/aiinfo/customer/' + id);
  const d = response.data || {};
  // Guard every section so a partial/malformed response degrades gracefully
  // (an absent section renders empty) rather than crashing the tab.
  return {
    customer: d.customer ?? null,
    qa: Array.isArray(d.qa) ? d.qa : [],
    nicknames: Array.isArray(d.nicknames) ? d.nicknames : [],
    shorthand_local: Array.isArray(d.shorthand_local) ? d.shorthand_local : [],
    shorthand_netsuite: Array.isArray(d.shorthand_netsuite) ? d.shorthand_netsuite : [],
    recent_items: Array.isArray(d.recent_items) ? d.recent_items : [],
    pallet: Array.isArray(d.pallet) ? d.pallet : [],
    corrections: Array.isArray(d.corrections) ? d.corrections : [],
    errors: (d.errors && typeof d.errors === 'object') ? d.errors : {},
  };
};

// Nickname (customer alias) edit / soft-deactivate. POST /aiinfo/nickname/{id}.
export const editNickname = async (
  id: number,
  body: { nickname?: string; customer_name?: string }
): Promise<any> => {
  const response = await api.post('/aiinfo/nickname/' + id, { action: 'edit', ...body });
  return response.data;
};

export const deactivateNickname = async (id: number): Promise<any> => {
  const response = await api.post('/aiinfo/nickname/' + id, { action: 'deactivate' });
  return response.data;
};

// Local shorthand override edit / soft-deactivate. POST /aiinfo/shorthand/{id}.
// NetSuite shorthand is read-only reference and is NOT editable through here.
export const editShorthandOverride = async (
  id: number,
  body: {
    shorthand?: string;
    item_id?: number;
    item_name?: string;
    default_quantity?: number | null;
    default_unit?: string | null;
  }
): Promise<any> => {
  const response = await api.post('/aiinfo/shorthand/' + id, { action: 'edit', ...body });
  return response.data;
};

export const deactivateShorthandOverride = async (id: number): Promise<any> => {
  const response = await api.post('/aiinfo/shorthand/' + id, { action: 'deactivate' });
  return response.data;
};

// Promote a customer-scoped learning to 'general' (applies to ALL customers).
// POST /aiinfo/{kind}/{id} {action:'promote'} -> scope='general'. The backend
// REFUSES (HTTP 400) promoting a shorthand whose item is a custom blend (blends
// stay customer-scoped) — callers should surface that error inline.
export const promoteNickname = async (id: number): Promise<any> => {
  const response = await api.post('/aiinfo/nickname/' + id, { action: 'promote' });
  return response.data;
};

export const promoteShorthand = async (id: number): Promise<any> => {
  const response = await api.post('/aiinfo/shorthand/' + id, { action: 'promote' });
  return response.data;
};

// Pallet override upsert / soft-deactivate — thin wrappers over the EXISTING
// pallet-conversions API (POST upserts by item_id; DELETE is now a soft delete
// on the backend per AIINFO-v2.0 Q2). Only the `source:'override'` pallet rows
// are editable; netsuite/default rows are read-only reference.
export const upsertPalletOverride = async (
  itemId: number,
  bagsPerPallet: number,
  itemName?: string
): Promise<any> => {
  const response = await api.post('/pallet-conversions', {
    item_id: itemId,
    bags_per_pallet: bagsPerPallet,
    item_name: itemName || '',
  });
  return response.data;
};

export const deactivatePalletOverride = async (itemId: number): Promise<any> => {
  const response = await api.delete('/pallet-conversions', { data: { item_id: itemId } });
  return response.data;
};

export default api;
